import {
  DRUM_CHANNEL,
  MIDI_VELOCITY_MAX,
  type Midi,
  type MidiNote,
} from "@midi-fantom-utils/midi-core";

export type PlaybackNote = {
  channel: number;
  startTicks: number;
  durationTicks: number;
  midi: number;
  velocity: number;
  /**
   * Номер GM-программы (0..127) на момент старта ноты.
   * Резолвится во время `buildPlaybackData` по program-change-событиям
   * трека (`track.programChanges`).
   * Для канала 9 — всегда `0` (GM drum map применяется автоматически
   * в `Soundfont2`).
   */
  program: number;
  /**
   * Флаг ударной ноты. true, если трек объявлен percussion
   * (track.instrument.percussion === true) или канал === 9.
   * Используется в soundfontEngine для маршрутизации на ударный пресет.
   */
  isDrum: boolean;
};

export type PlaybackTempo = {
  bpm: number;
  ticks: number;
};

export type PlaybackData = {
  ppq: number;
  tempos: PlaybackTempo[];
  notes: PlaybackNote[];
};

const PROGRAM_MAX = 127;

type ProgramChange = { ticks: number; value: number };

const toVelocityInt = (normalized: number): number => {
  if (!Number.isFinite(normalized) || normalized <= 0) return 1;
  const v = Math.round(normalized * MIDI_VELOCITY_MAX);
  if (v < 1) return 1;
  if (v > MIDI_VELOCITY_MAX) return MIDI_VELOCITY_MAX;
  return v;
};

const sanitizeNote = (
  note: MidiNote,
  channel: number,
  program: number,
  isDrum: boolean
): PlaybackNote | null => {
  if (!Number.isFinite(note.midi) || note.midi < 0 || note.midi > 127) return null;
  const startTicks = Math.max(0, Math.round(note.ticks));
  const durationTicks = Math.max(1, Math.round(note.durationTicks));
  return {
    channel,
    startTicks,
    durationTicks,
    midi: Math.round(note.midi),
    velocity: toVelocityInt(note.velocity),
    program,
    isDrum,
  };
};

const clampProgram = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(PROGRAM_MAX, Math.round(value)));
};

const collectProgramChanges = (track: Midi["tracks"][number]): ProgramChange[] => {
  return track.programChanges
    .filter((pc) => Number.isFinite(pc.ticks) && Number.isFinite(pc.value))
    .map((pc) => ({ ticks: Math.max(0, Math.round(pc.ticks)), value: pc.value }))
    .sort((a, b) => a.ticks - b.ticks);
};

/**
 * Чистая функция: возвращает GM-программу, активную на момент `tick`
 * (в тиках PPQ) с учётом program-change событий.
 *
 * - `isDrum === true` — всегда возвращает `0` (GM drum map):
 *   звуковой движок сам маршрутизирует канал 9 на ударный пресет,
 *   переданный program игнорируется.
 * - `programChanges` должен быть отсортирован по `ticks` ascending.
 * - `defaultProgram` — значение, возвращаемое, если до `tick` нет ни одного
 *   program-change (например, `track.instrument.number` источника).
 */
export const resolveProgramAt = (
  programChanges: ReadonlyArray<ProgramChange>,
  tick: number,
  isDrum: boolean,
  defaultProgram: number = 0
): number => {
  if (isDrum) return 0;
  let resolved: number | null = null;
  for (const pc of programChanges) {
    if (pc.ticks <= tick) {
      resolved = pc.value;
    } else {
      break;
    }
  }
  if (resolved === null) {
    return clampProgram(defaultProgram);
  }
  return clampProgram(resolved);
};

export const buildPlaybackData = (midi: Midi): PlaybackData => {
  const notes: PlaybackNote[] = [];
  for (const track of midi.tracks) {
    if (track.notes.length === 0) continue;
    const trackChannel = track.channel;
    if (trackChannel < 0 || trackChannel > 15) continue;
    const programChanges = collectProgramChanges(track);
    // Формат-0 агрегат (несколько каналов в одном MTrk): percussion-флаг
    // трека не отражает per-channel роль, поэтому для multi-channel трека
    // ударность определяется только каналом ноты (как в key-detect).
    const singleChannel = track.notes.every(
      (n) => (n.channel ?? trackChannel) === trackChannel
    );
    const isDrumTrack = singleChannel && track.instrument.percussion;
    for (const note of track.notes) {
      const channel = note.channel ?? trackChannel;
      const isDrum = isDrumTrack || channel === DRUM_CHANNEL;
      // Если у канала есть хотя бы один PC — берём первый как `defaultProgram`
      // для нот до этого PC. Без PC fallback на `track.instrument.number`
      // (последний PC трека). Это восстанавливает корректное переключение
      // инструментов в плеере, которое ломалось из-за того, что парсер
      // сохранял только последний PC в `track.instrument.number`.
      const defaultProgram = isDrum
        ? 0
        : programChanges.length > 0
          ? clampProgram(programChanges[0]!.value)
          : clampProgram(track.instrument.number);
      const program = resolveProgramAt(
        programChanges,
        note.ticks,
        isDrum,
        defaultProgram
      );
      const cleaned = sanitizeNote(note, channel, program, isDrum);
      if (cleaned) notes.push(cleaned);
    }
  }
  notes.sort((a, b) => a.startTicks - b.startTicks || a.channel - b.channel);

  const tempos: PlaybackTempo[] = midi.header.tempos
    .map((t) => ({ bpm: t.bpm, ticks: Math.max(0, Math.round(t.ticks)) }))
    .filter((t) => Number.isFinite(t.bpm) && t.bpm > 0)
    .sort((a, b) => a.ticks - b.ticks);

  const ppqRaw = midi.header.ppq;
  const ppq = Number.isFinite(ppqRaw) && ppqRaw > 0 ? Math.round(ppqRaw) : 480;

  return { ppq, tempos, notes };
};